import { useState } from 'react'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  SxProps, TextField, Theme, Typography, useTheme
} from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import { EnergySummary, PowerData, PriceData, ScaleLabeling } from '../types/charts'
import { useAxios, useAxioses } from '../hooks/useAxios'
import { Port, TimeString } from '../types/models'
import { Trans, useTranslation } from 'react-i18next'
import { useApp } from '../hooks/useApp'
import { VAT } from '../utils/vat'
import { RadioSelector } from '../components/atoms/RadioSelector'
import { numeric } from '../utils/num'
import { Series } from '../utils/series'
import { Sym } from './atoms/Sym'
import { range, countryToTimezone, dayStartISO } from '../utils/time'
import { isDebug } from '../utils/misc'
import { DateTime } from 'luxon'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'

export interface HistoryStatsProps {
  port: Port
  /** Whether the import figures include margin and transfer fee (the fee switch of the History page). */
  includeFees?: boolean
}

export function HistoryStats(props: HistoryStatsProps) {

  const app = useApp()
  const { t } = useTranslation()
  const [scale, setScale] = useState<ScaleLabeling>('24h')
  const [customStartDate, setCustomStartDate] = useState<DateTime | null>(null)
  const [customEndDate, setCustomEndDate] = useState<DateTime | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const theme = useTheme()
  const { port, includeFees = false } = props

  // Dedicated solar ports are pure production — keep the production-only view.
  // Consumption meters can both import and export, so they get the split view.
  const isSolar = port.type === 'SOLAR'

  const country = app.user?.country?.iso_3166 || 'FI'
  const currency = app.user?.currency || 'EUR'
  const timezone = countryToTimezone(country)

  const handleScaleChange = (value: string) => {
    setScale(value as ScaleLabeling)
    // Initialize custom end date to today when switching to 'all'
    if (value === 'all' && !customEndDate) {
      setCustomEndDate(DateTime.now().setZone(timezone).startOf('day'))
    }
  }
  // TODO: This should be handled in billing() function instead.
  // const vat = app.user?.settings?.contractType === 'FIXED' ? 0.0 : VAT.for(country)
  const vat = VAT.for(country)

  const [data, setData] = useState<PowerData[]>([])
  const [prices, setPrices] = useState<PriceData[]>([])
  const [summary, setSummary] = useState<EnergySummary | null>(null)
  const [oldestDate, setOldestDate] = useState<TimeString | null>(null)

  // Determine actual date range - use custom dates if available, otherwise use scale-based range.
  // The pickers hand out an invalid DateTime for a half-typed date, which has no
  // ISO representation at all; such a date is ignored until it is complete.
  const scaleMinDate = range(scale, oldestDate, 0, timezone)[0]
  const scaleMaxDate = range(scale, oldestDate, 0, timezone)[1]
  const customMinDate = scale === 'all' ? dayStartISO(customStartDate, timezone) : null
  const customMaxDate = scale === 'all' ? dayStartISO(customEndDate, timezone) : null
  const minDate = customMinDate || scaleMinDate
  const maxDate = customMaxDate || scaleMaxDate

  // Guards against firing requests for a window that is not usable: a date the
  // user has not finished typing, or a zero-length window ('all' before the
  // port's oldest timestamp is known, or a port whose data starts today). A
  // window the port only partially covers is *not* gated — the figures are
  // whatever exists inside it, see range() in utils/time.
  const rangeReady = (oldestDate !== null || scale !== 'all') && !!minDate && !!maxDate && minDate !== maxDate

  // Solar ports: aggregate from raw power + prices (single net figure is fine
  // for pure production).
  const { consumption, price, avgPrice, avgMarketPrice: solarMarketPrice } = isSolar && rangeReady
    ? Series.averages(data, prices, { vat, user: app.user?.settings })
    : { consumption: null, price: null, avgPrice: null, avgMarketPrice: null }

  // Consumption meters: import and export are billed separately (15-minute
  // netting), so the server returns them split. VAT applies to imports only.
  const { importEnergy, exportEnergy, avgImportPrice, avgExportPrice, netPrice, avgMarketPrice } =
    Series.consumptionStats(summary, vat, includeFees)

  useAxios<{ energy: TimeString }>({
    url: `/history/oldest/${port.id}`,
    onSuccess: (data) => {
      setOldestDate(data.energy)
    }
  })

  useAxioses<PriceData[], PowerData[]>({
    url: `/data/prices?from=${minDate}&to=${DateTime.fromISO(maxDate).plus({ hours: 1 }).toUTC().toISO()}&area=${country}`,
    disabled: !isSolar || !rangeReady,
  },
  {
    url: `/history/power/${port.id}?from=${minDate}&to=${maxDate}`,
    onSuccess: (prices, power) => {
      setPrices(prices)
      setData(power)
    }
  })

  useAxios<EnergySummary>({
    url: `/history/energy_summary/${port.id}?from=${minDate}&to=${maxDate}`,
    disabled: isSolar || !rangeReady,
    onSuccess: (s) => setSummary(s),
  })

  const sxStatBox: SxProps<Theme> = {
    backgroundColor: theme.palette.statsHistoryBackground.main,
    borderRadius: '0.375rem',
    height: '5.6875rem',
    lineHeight: '1.1rem',
    p: 2,
  }

  const sxTopLine: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    lineHeight: '2.2rem'
  }

  const sxMainFigure: SxProps<Theme> = {
    color: theme.palette.chartText.main,
    fontWeight: 400,
    fontSize: '2rem',
    whiteSpace: 'nowrap'
  }

  const sxSideFigure: SxProps<Theme> = {
    fontWeight: 400,
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
    textAlign: 'right',
    transform: 'translateY(0.4rem)',
  }

  const kwhDecimals = isDebug('high-accuracy') ? 3 : 1
  const eurDecimals = isDebug('high-accuracy') ? 4 : 2

  const statBox = (figure: JSX.Element, label: JSX.Element) =>
    <Box sx={ sxStatBox }>
      <Box sx={ sxTopLine }>
        <Box sx={ sxMainFigure }>{figure}</Box>
        <Box sx={ sxSideFigure }></Box>
      </Box>
      {label}
    </Box>

  // With the fee switch on, the import figures include margin and transfer fee: one sentence says so.
  const withFees = (text: string) => (includeFees ? `${text} ${t('Includes the margin and transfer fee from your settings.')}` : text)

  // Help entries explaining each statistic, shown in the help dialog.
  const helpItems: { term: string, text: string }[] = [
    {
      term: t('Time range'),
      text: t('The most recent period the statistics are shown for, for example 24 hours back from the current moment.'),
    },
    {
      term: t('Energy import'),
      text: t('The total energy bought from the grid, over the quarter-hour periods when the meter was on the import side.'),
    },
    {
      term: t('Energy export'),
      text: t('The total energy sold to the grid (overproduction), over the quarter-hour periods when the meter was on the export side.'),
    },
    {
      term: t('Average import price'),
      text: withFees(t('The VAT-inclusive, consumption-weighted average spot price for the quarter-hours when buying from the grid. Comparable to the average market energy price.')),
    },
    {
      term: t('Average export price'),
      text: t('The VAT-free average spot price of overproduction for the quarter-hours when selling to the grid.'),
    },
    {
      term: t('Price'),
      text: withFees(t('The cost of imported energy (with VAT), minus the value of exported energy (without VAT and without transfer fees).')),
    },
    {
      term: t('Average market energy price'),
      text: withFees(t('The average VAT-inclusive spot price over the same period.')),
    },
  ]

  return <Box>
    <Box sx={{ position: 'relative', mb: 2, display: 'flex', justifyContent: 'center' }}>
      <RadioSelector
        variant="gray"
        value={scale}
        onClick={handleScaleChange}
        values={{
          '24h': t('Scale-24h'),
          '7d': t('Scale-7d'),
          '30d': t('Scale-30d'),
          year: t('Scale-Year'),
          all: t('Scale-All'),
        }}
      />
      {!isSolar && (
        <IconButton
          size="small"
          aria-label={t('Help')}
          onClick={() => setHelpOpen(true)}
          sx={{ position: 'absolute', right: 0, top: 0 }}
        >
          <HelpOutline fontSize="small" />
        </IconButton>
      )}
    </Box>

    <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('Help')}</DialogTitle>
      <DialogContent dividers>
        {helpItems.map((item) =>
          <Box key={item.term} sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>{item.term}</Typography>
            <Typography variant="body2" color="text.secondary">{item.text}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setHelpOpen(false)}>{t('Close')}</Button>
      </DialogActions>
    </Dialog>

    {scale === 'all' && (
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
          <DatePicker<DateTime>
            label={t('Start Date')}
            value={customStartDate}
            onChange={(newValue: DateTime | null) => setCustomStartDate(newValue)}
            maxDate={customEndDate || DateTime.now()}
            renderInput={(params) => <TextField {...params} size="small" fullWidth />}
          />
          <DatePicker<DateTime>
            label={t('End Date')}
            value={customEndDate}
            onChange={(newValue: DateTime | null) => setCustomEndDate(newValue)}
            minDate={customStartDate || undefined}
            maxDate={DateTime.now()}
            renderInput={(params) => <TextField {...params} size="small" fullWidth />}
          />
        </Box>
      </LocalizationProvider>
    )}

    {scale === 'all' && (customStartDate?.isValid || customEndDate?.isValid) && (
      <Box sx={{ textAlign: 'center', mb: 2, fontSize: '0.875rem', color: theme.palette.text.secondary }}>
        {t('Showing data from {{start}} to {{end}}', {
          start: customStartDate?.isValid ? customStartDate.toLocaleString() : t('Beginning'),
          end: customEndDate?.isValid ? customEndDate.toLocaleString() : t('Now')
        })}
      </Box>
    )}

    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      columnGap: '0.5rem',
      rowGap: '0.5rem',
      fontWeight: 400,
      fontSize: '0.8rem',
      lineHeight: '0.9375rem',
      mb: 4,
    }}>
      {isSolar
        ? <>
          {statBox(
            <>{numeric(consumption, kwhDecimals)} kWh</>,
            <Trans>Energy produced</Trans>
          )}
          {statBox(
            <>{numeric(price, eurDecimals)} <Sym currency={currency}/></>,
            <Trans>Price</Trans>
          )}
          {statBox(
            <>{numeric(avgPrice, eurDecimals, null)} <Sym currency={currency}/></>,
            <Trans>Average value of production</Trans>
          )}
          {statBox(
            <>{numeric(solarMarketPrice, eurDecimals, null)} <Sym currency={currency}/></>,
            <Trans>Average market energy price</Trans>
          )}
        </>
        : <>
          {statBox(
            <>{numeric(importEnergy, kwhDecimals, null)} kWh</>,
            <Trans>Energy import</Trans>
          )}
          {statBox(
            <>{numeric(exportEnergy, kwhDecimals, null)} kWh</>,
            <Trans>Energy export</Trans>
          )}
          {statBox(
            <>{numeric(avgImportPrice, eurDecimals, null)} <Sym currency={currency}/></>,
            <Trans>Average import price</Trans>
          )}
          {statBox(
            <>{numeric(avgExportPrice, eurDecimals, null)} <Sym currency={currency}/></>,
            <Trans>Average export price</Trans>
          )}
          {statBox(
            <>{numeric(netPrice, eurDecimals, null)} <Sym currency={currency}/></>,
            <Trans>Price</Trans>
          )}
          {statBox(
            <>{numeric(avgMarketPrice, eurDecimals, null)} <Sym currency={currency}/></>,
            <Trans>Average market energy price</Trans>
          )}
        </>}
    </Box>
  </Box>
}
